import type {
  BriefComponentData,
  ComponentData,
  QuoteItemBaqData,
  QuoteListResultData,
} from '@frontend/api';

import type { RootState } from 'store/configureStore';
import { INIT_EDIT_QUOTE } from 'store/quotes/types';
import type { ActionWithPayload } from 'store/types';

import { types } from './actions';

// Components in destinationComponentsMap can be either BriefComponentData (child) or ComponentData (root)
type DestinationComponent = BriefComponentData | ComponentData;

type CopyPricingDrawerAction =
  | ActionWithPayload<typeof INIT_EDIT_QUOTE>
  | ActionWithPayload<
      typeof types.INIT_COPY_PRICING_DRAWER,
      {
        isSourceItar: boolean;
        quote: QuoteListResultData;
        sourceComponent: ComponentData;
        quoteItems: QuoteItemBaqData[];
      }
    >
  | ActionWithPayload<typeof types.CLOSE_COPY_PRICING_DRAWER>
  | ActionWithPayload<
      typeof types.SET_SELECTED_QUOTE,
      { selectedQuote: QuoteListResultData }
    >
  | ActionWithPayload<
      typeof types.SET_IS_CHOOSING_QUOTE,
      { isChoosingQuote: boolean }
    >
  | ActionWithPayload<typeof types.GET_DRAFTED_QUOTES_REQUEST>
  | ActionWithPayload<
      typeof types.GET_DRAFTED_QUOTES_SUCCESS,
      { draftedQuotes: QuoteListResultData[]; hasMoreQuotes: boolean }
    >
  | ActionWithPayload<typeof types.GET_DRAFTED_QUOTES_ERROR, unknown>
  | ActionWithPayload<
      typeof types.GET_ASSEMBLY_COMPONENTS_REQUEST,
      { rootComponentId: number }
    >
  | ActionWithPayload<
      typeof types.GET_ASSEMBLY_COMPONENTS_SUCCESS,
      { rootComponentId: number; components: BriefComponentData[] }
    >
  | ActionWithPayload<
      typeof types.SELECT_COMPONENT,
      { component: DestinationComponent }
    >
  | ActionWithPayload<
      typeof types.DESELECT_COMPONENT,
      { component: DestinationComponent }
    >
  | ActionWithPayload<typeof types.GET_QUOTE_ITEMS_REQUEST>
  | ActionWithPayload<
      typeof types.GET_QUOTE_ITEMS_SUCCESS,
      { quoteItems: QuoteItemBaqData[] }
    >
  | ActionWithPayload<typeof types.GET_QUOTE_ITEMS_ERROR, unknown>
  | ActionWithPayload<
      typeof types.SET_IS_COPYING_ADD_ONS,
      { isCopyingAddOns: boolean }
    >
  | ActionWithPayload<
      typeof types.SET_IS_COPYING_QUANTITIES,
      { isCopyingQuantities: boolean }
    >
  | ActionWithPayload<
      typeof types.SET_IS_COPYING_MATERIALS,
      { isCopyingMaterials: boolean }
    >
  | ActionWithPayload<
      typeof types.SET_IS_COPYING_OPERATIONS,
      { isCopyingOperations: boolean }
    >
  | ActionWithPayload<
      typeof types.SET_IS_COPYING_PRICING_ITEMS,
      { isCopyingPricingItems: boolean }
    >
  | ActionWithPayload<
      typeof types.SET_IS_COPYING_DISCOUNTS,
      { isCopyingDiscounts: boolean }
    >
  | ActionWithPayload<typeof types.COPY_PRICING_REQUEST>
  | ActionWithPayload<typeof types.COPY_PRICING_SUCCESS>
  | ActionWithPayload<typeof types.COPY_PRICING_ERROR, unknown>
  | ActionWithPayload<typeof types.GET_ADDITIONAL_QUOTES_REQUEST>
  | ActionWithPayload<
      typeof types.GET_ADDITIONAL_QUOTES_SUCCESS,
      { quotes: QuoteListResultData[]; page: number; hasMoreQuotes: boolean }
    >
  | ActionWithPayload<typeof types.GET_ADDITIONAL_QUOTES_ERROR, unknown>
  | ActionWithPayload<
      typeof types.SET_ASSEMBLY_EXPANDED,
      { quoteItemId: number; isExpanded: boolean }
    >;

type CopyPricingDrawerState = {
  assemblyComponentsMap: Record<number | string, BriefComponentData[]>;
  draftedQuotes: QuoteListResultData[];
  isCopyingAddOns: boolean;
  isCopyingQuantities: boolean;
  isCopyingOperations: boolean;
  isCopyingMaterials: boolean;
  isCopyingPricingItems: boolean;
  isCopyingDiscounts: boolean;
  isCopyingPricing: boolean;
  isCopyPricingDrawerOpen: boolean;
  isChoosingQuote: boolean;
  isFetchingAdditionalQuotes: boolean;
  isFetchingAssemblyComponentsMap: Record<number | string, boolean>;
  isFetchingQuotes: boolean;
  isFetchingQuoteItems: boolean;
  isAssemblyExpandedMap: Record<number, boolean>;
  quoteItems: QuoteItemBaqData[];
  quotesPage: number;
  hasMoreQuotes: boolean;
  sourceComponent: ComponentData | undefined;
  isSourceItar: boolean;
  destinationComponentsMap: Record<number, DestinationComponent>;
  selectedQuote?: QuoteListResultData;
  initialQuote?: QuoteListResultData;
};

const initialState: CopyPricingDrawerState = {
  assemblyComponentsMap: {},
  draftedQuotes: [],
  isCopyingAddOns: true,
  isCopyingQuantities: false,
  isCopyingOperations: true,
  isCopyingMaterials: true,
  isCopyingPricingItems: true,
  isCopyingDiscounts: true,
  isCopyingPricing: false,
  isCopyPricingDrawerOpen: false,
  isChoosingQuote: false,
  isFetchingAdditionalQuotes: false,
  isFetchingAssemblyComponentsMap: {},
  isFetchingQuotes: false,
  isFetchingQuoteItems: false,
  isAssemblyExpandedMap: {},
  quoteItems: [],
  quotesPage: 1,
  hasMoreQuotes: true,
  sourceComponent: undefined,
  isSourceItar: false,
  destinationComponentsMap: {},
};

const reducer = (
  state = initialState,
  action: CopyPricingDrawerAction,
): CopyPricingDrawerState => {
  switch (action.type) {
    case INIT_EDIT_QUOTE:
      return { ...initialState };
    case types.INIT_COPY_PRICING_DRAWER: {
      const { isSourceItar, quoteItems, sourceComponent, quote } =
        action.payload;
      return {
        ...initialState,
        isSourceItar,
        quoteItems,
        sourceComponent,
        selectedQuote: quote,
        initialQuote: quote,
        isCopyPricingDrawerOpen: true,
      };
    }
    case types.CLOSE_COPY_PRICING_DRAWER:
      return {
        ...state,
        isCopyPricingDrawerOpen: false,
      };
    case types.SET_SELECTED_QUOTE: {
      const { selectedQuote } = action.payload;
      return {
        ...state,
        selectedQuote,
        isChoosingQuote: false,
      };
    }
    case types.SET_IS_CHOOSING_QUOTE: {
      const { isChoosingQuote } = action.payload;
      return {
        ...state,
        isChoosingQuote,
        quotesPage: 1,
      };
    }
    case types.GET_DRAFTED_QUOTES_REQUEST:
      return {
        ...state,
        isFetchingQuotes: true,
      };
    case types.GET_DRAFTED_QUOTES_SUCCESS: {
      const { draftedQuotes, hasMoreQuotes } = action.payload;
      return {
        ...state,
        isFetchingQuotes: false,
        draftedQuotes,
        hasMoreQuotes,
      };
    }
    case types.GET_DRAFTED_QUOTES_ERROR:
      return {
        ...state,
        isFetchingQuotes: false,
      };
    case types.GET_ASSEMBLY_COMPONENTS_SUCCESS: {
      const { rootComponentId, components } = action.payload;
      const isFetchingAssemblyComponentsMap = {
        ...state.isFetchingAssemblyComponentsMap,
      };
      const assemblyComponentsMap = { ...state.assemblyComponentsMap };
      isFetchingAssemblyComponentsMap[rootComponentId] = false;
      assemblyComponentsMap[rootComponentId] = components;
      return {
        ...state,
        isFetchingAssemblyComponentsMap,
        assemblyComponentsMap,
      };
    }
    case types.GET_ASSEMBLY_COMPONENTS_REQUEST: {
      const { rootComponentId } = action.payload;
      const isFetchingAssemblyComponentsMap = {
        ...state.isFetchingAssemblyComponentsMap,
      };
      isFetchingAssemblyComponentsMap[rootComponentId] = true;
      return {
        ...state,
        isFetchingAssemblyComponentsMap,
      };
    }
    case types.SELECT_COMPONENT: {
      const { component } = action.payload;
      const destinationComponentsMap = { ...state.destinationComponentsMap };
      destinationComponentsMap[component.id] = component;
      return {
        ...state,
        destinationComponentsMap,
      };
    }
    case types.DESELECT_COMPONENT: {
      const { component } = action.payload;
      const destinationComponentsMap = { ...state.destinationComponentsMap };
      delete destinationComponentsMap[component.id];
      return {
        ...state,
        destinationComponentsMap,
      };
    }
    case types.GET_QUOTE_ITEMS_REQUEST:
      return {
        ...state,
        isFetchingQuoteItems: true,
      };
    case types.GET_QUOTE_ITEMS_SUCCESS: {
      const { quoteItems } = action.payload;
      return {
        ...state,
        quoteItems,
        isFetchingQuoteItems: false,
      };
    }
    case types.GET_QUOTE_ITEMS_ERROR:
      return {
        ...state,
        isFetchingQuoteItems: false,
      };
    case types.SET_IS_COPYING_ADD_ONS: {
      const { isCopyingAddOns } = action.payload;
      return {
        ...state,
        isCopyingAddOns,
      };
    }
    case types.SET_IS_COPYING_QUANTITIES: {
      const { isCopyingQuantities } = action.payload;
      return {
        ...state,
        isCopyingQuantities,
      };
    }
    case types.SET_IS_COPYING_MATERIALS: {
      const { isCopyingMaterials } = action.payload;
      return {
        ...state,
        isCopyingMaterials,
      };
    }
    case types.SET_IS_COPYING_OPERATIONS: {
      const { isCopyingOperations } = action.payload;
      return {
        ...state,
        isCopyingOperations,
      };
    }
    case types.SET_IS_COPYING_PRICING_ITEMS: {
      const { isCopyingPricingItems } = action.payload;
      return {
        ...state,
        isCopyingPricingItems,
      };
    }
    case types.SET_IS_COPYING_DISCOUNTS: {
      const { isCopyingDiscounts } = action.payload;
      return {
        ...state,
        isCopyingDiscounts,
      };
    }
    case types.COPY_PRICING_REQUEST:
      return {
        ...state,
        isCopyingPricing: true,
      };
    case types.COPY_PRICING_SUCCESS:
      return {
        ...state,
        isCopyingPricing: false,
        isCopyPricingDrawerOpen: false,
      };
    case types.COPY_PRICING_ERROR:
      return {
        ...state,
        isCopyingPricing: false,
      };
    case types.GET_ADDITIONAL_QUOTES_REQUEST:
      return {
        ...state,
        isFetchingAdditionalQuotes: true,
      };
    case types.GET_ADDITIONAL_QUOTES_SUCCESS: {
      const { quotes, page, hasMoreQuotes } = action.payload;
      const draftedQuotes = [...state.draftedQuotes, ...quotes];
      return {
        ...state,
        isFetchingAdditionalQuotes: false,
        draftedQuotes,
        quotesPage: page,
        hasMoreQuotes,
      };
    }
    case types.GET_ADDITIONAL_QUOTES_ERROR:
      return {
        ...state,
        isFetchingAdditionalQuotes: false,
      };
    case types.SET_ASSEMBLY_EXPANDED: {
      const { quoteItemId, isExpanded } = action.payload;
      const isAssemblyExpandedMap = { ...state.isAssemblyExpandedMap };
      isAssemblyExpandedMap[quoteItemId] = isExpanded;
      return {
        ...state,
        isAssemblyExpandedMap,
      };
    }
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): CopyPricingDrawerState =>
  state.modules.quotes.copyPricingDrawer;

export const isCopyPricingDrawerOpen = (state: RootState) =>
  getScope(state).isCopyPricingDrawerOpen;

export const getQuoteItems = (state: RootState) => getScope(state).quoteItems;

export const getSourceComponent = (state: RootState) =>
  getScope(state).sourceComponent;

export const getIsSourceItar = (state: RootState) =>
  getScope(state).isSourceItar;

export const isChoosingQuote = (state: RootState) =>
  getScope(state).isChoosingQuote;

export const isFetchingDraftedQuotes = (state: RootState) =>
  getScope(state).isFetchingQuotes;

export const getDraftedQuotes = (state: RootState) =>
  getScope(state).draftedQuotes;

export const getSelectedQuote = (state: RootState) =>
  getScope(state).selectedQuote;

export const getInitialQuote = (state: RootState) =>
  getScope(state).initialQuote;

export const getIsFetchingAssemblyComponentsMap = (state: RootState) =>
  getScope(state).isFetchingAssemblyComponentsMap;

export const getAssemblyComponentsMap = (state: RootState) =>
  getScope(state).assemblyComponentsMap;

export const getIsAssemblyExpandedMap = (state: RootState) =>
  getScope(state).isAssemblyExpandedMap;

export const getIsAssemblyExpanded = (state: RootState, quoteItemId: number) =>
  Boolean(getIsAssemblyExpandedMap(state)[quoteItemId]);

export const getDestinationComponentsMap = (state: RootState) =>
  getScope(state).destinationComponentsMap;

export const getIsComponentSelected = (state: RootState, componentId: number) =>
  Boolean(getDestinationComponentsMap(state)[componentId]);

export const isFetchingQuoteItems = (state: RootState) =>
  getScope(state).isFetchingQuoteItems;

export const isCopyingAddOns = (state: RootState) =>
  getScope(state).isCopyingAddOns;

export const isCopyingQuantities = (state: RootState) =>
  getScope(state).isCopyingQuantities;

export const isCopyingMaterials = (state: RootState) =>
  getScope(state).isCopyingMaterials;

export const isCopyingOperations = (state: RootState) =>
  getScope(state).isCopyingOperations;

export const isCopyingPricingItems = (state: RootState) =>
  getScope(state).isCopyingPricingItems;

export const isCopyingDiscounts = (state: RootState) =>
  getScope(state).isCopyingDiscounts;

export const getDestinationComponentsArray = (state: RootState) =>
  Object.values(getScope(state).destinationComponentsMap);

export const hasDestinationComponentsSelected = (state: RootState) =>
  getDestinationComponentsArray(state).length > 0;

export const hasSubComponentSelected = (state: RootState) =>
  Boolean(
    getDestinationComponentsArray(state).find(
      (comp) => !('isRootComponent' in comp) || !comp.isRootComponent,
    ),
  );

export const hasSourcePropertySelected = (state: RootState) =>
  isCopyingAddOns(state) ||
  isCopyingMaterials(state) ||
  isCopyingOperations(state) ||
  isCopyingPricingItems(state) ||
  isCopyingDiscounts(state) ||
  isCopyingQuantities(state);

export const getSelectedProperties = (state: RootState) => {
  const scope = getScope(state);
  return {
    isCopyingAddOns: scope.isCopyingAddOns,
    isCopyingQuantities: scope.isCopyingQuantities,
    isCopyingMaterials: scope.isCopyingMaterials,
    isCopyingPricingItems: scope.isCopyingPricingItems,
    isCopyingDiscounts: scope.isCopyingDiscounts,
    isCopyingOperations: scope.isCopyingOperations,
  };
};

export const isCopyingPricing = (state: RootState) =>
  getScope(state).isCopyingPricing;

export const getQuotesPage = (state: RootState) => getScope(state).quotesPage;

export const isFetchingAdditionalQuotes = (state: RootState) =>
  getScope(state).isFetchingAdditionalQuotes;

export const hasMoreQuotes = (state: RootState) =>
  getScope(state).hasMoreQuotes;
